import {
  Button,
  Grid2 as Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
  LinearProgress,
  Box,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { useState, useCallback } from "react";
import { Form } from "@shared/components";
import { getAgreementsService } from "@modules/agreements/services/queries/listAgreements/service";
import { Agreement } from "@modules/agreements/types";
import { validateService } from "@modules/integration/services/mutations/service";
import { CheckCircle, Error as ErrorIcon, FileDownload } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

interface TestResult {
  agreement: Agreement;
  status: "success" | "error" | "pending";
  errors: string[];
  result?: any;
}

export function IntegrationBulk() {
  const theme = useTheme();
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<TestResult[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalAgreements, setTotalAgreements] = useState(0);
  const [agreements, setAgreements] = useState<Agreement[]>([]);

  const formMethods = useForm({
    defaultValues: {
      cpf: "",
      integration_id: "",
      product_module: "",
    },
  });

  const integrationOptions = [
    { value: "nação", label: "Flamengo" },
    { value: "tim", label: "Tim" },
    { value: "ixc", label: "IXC" },
    { value: "mk", label: "MK" },
    { value: "sgp", label: "SGP" },
    { value: "playhub", label: "Playhub" },
    { value: "dtel", label: "Dtel" },
    { value: "rbx", label: "RBX" },
    { value: "elite", label: "Elite" },
    { value: "easy", label: "Easy" },
    { value: "top", label: "Top" },
    { value: "itnet", label: "ItNet" },
    { value: "az", label: "7az" },
    { value: "youcast!", label: "youcast" },
    { value: "duobox", label: "duobox" },
    { value: "gfsis", label: "GFsis" },
    { value: "neo", label: "NEO" },
    { value: "certirio", label: "Certirio" },
    { value: "isuper", label: "iSuper" },
    { value: "voalle", label: "Voalle" },
    { value: "hubsoft", label: "Hubsoft" },
    { value: "sercomtel", label: "Sercomtel" },
  ];

  const fetchAgreementsWithIntegration = useCallback(
    async (integrationId?: string, productModule?: boolean) => {
      const allAgreements: Agreement[] = [];
      let page = 1;
      let hasMore = true;

      try {
        while (hasMore) {
          const pageAgreements = await getAgreementsService({
            page,
            perPage: 100,
            active: true,
            integration_id: integrationId || undefined,
            product_module: productModule !== undefined ? productModule : undefined,
          });

          if (pageAgreements.length === 0) {
            hasMore = false;
          } else {
            allAgreements.push(...pageAgreements);
            page++;
          }
        }
      } catch (error) {
        console.error("Error fetching agreements:", error);
        // Continue even if error occurs, return whatever has been collected so far
      }
      // Filtrar apenas convênios com integration_id
      const agreementsWithIntegration = allAgreements.filter(
        (agreement) => agreement.integration_id !== null
      );

      return agreementsWithIntegration;
    },
    []
  );

  const testAgreement = async (
    agreement: Agreement,
    cpf: string
  ): Promise<TestResult> => {
    try {
      const result = await validateService({
        cpf: cpf.replace(/\D/g, ""),
        agreement_id: agreement.id.toString(),
      });

      // Verificar se houve erro
      const hasError =
        !result?.result &&
        !result?.token &&
        (result?.error || result?.integration_error);

      const errors: string[] = [];

      if (result?.error) {
        errors.push(`Erro Partiu: ${result.error}`);
      }

      if (typeof result?.integration_error === "string") {
        errors.push(`Erro na integração: ${result.integration_error}`);
      }

      if (Array.isArray(result?.integration_error)) {
        result.integration_error.forEach((error: string) => {
          errors.push(error);
        });
      }

      if (result?.message && hasError) {
        errors.push(result.message);
      }

      return {
        agreement,
        status: hasError ? "error" : "success",
        errors,
        result,
      };
    } catch (error: any) {
      return {
        agreement,
        status: "error",
        errors: [
          error?.response?.data?.error ||
            error?.message ||
            "Erro desconhecido ao testar integração",
        ],
      };
    }
  };

  const runTests = async (cpf: string, integrationId?: string, productModule?: boolean) => {
    setIsRunning(true);
    setResults([]);
    setCurrentIndex(0);

    try {
      // Buscar todos os convênios com integração
      const agreementsWithIntegration = await fetchAgreementsWithIntegration(
        integrationId,
        productModule
      );
      setAgreements(agreementsWithIntegration);
      setTotalAgreements(agreementsWithIntegration.length);

      if (agreementsWithIntegration.length === 0) {
        setIsRunning(false);
        setResults([
          {
            agreement: {} as Agreement,
            status: "error",
            errors: ["Nenhum convênio com integração configurada encontrado."],
          },
        ]);
        return;
      }

      // Inicializar resultados com status pending
      const initialResults: TestResult[] = agreementsWithIntegration.map(
        (agreement) => ({
          agreement,
          status: "pending",
          errors: [],
        })
      );
      setResults(initialResults);

      // Testar cada convênio sequencialmente
      for (let i = 0; i < agreementsWithIntegration.length; i++) {
        setCurrentIndex(i);
        const result = await testAgreement(
          agreementsWithIntegration[i],
          cpf
        );

        // Atualizar resultado específico
        setResults((prev) => {
          const newResults = [...prev];
          newResults[i] = result;
          return newResults;
        });

        // Aguardar 2 segundos antes do próximo teste
        if (i < agreementsWithIntegration.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }
    } catch (error) {
      console.error("Error running tests:", error);
    } finally {
      setIsRunning(false);
    }
  };

  const handleSubmit = formMethods.handleSubmit((data) => {
    if (!data.cpf) {
      return;
    }
    const productModule = data.product_module === "true" ? true : data.product_module === "false" ? false : undefined;
    runTests(data.cpf, data.integration_id || undefined, productModule);
  });

  const processedCount = results.filter(
    (r) => r.status !== "pending"
  ).length;
  const successCount = results.filter((r) => r.status === "success").length;
  const errorCount = results.filter((r) => r.status === "error").length;
  const progressPercentage =
    totalAgreements > 0 ? (processedCount / totalAgreements) * 100 : 0;

  const exportToExcel = () => {
    if (results.length === 0) return;

    // Preparar dados para exportação
    const exportData = results.map((result, index) => ({
      "Nº": index + 1,
      "Convênio": result.agreement?.name || "N/A",
      "ID do Convênio": result.agreement?.id || "N/A",
      "Plataforma": result.agreement?.integration_id || "N/A",
      "Erros": result.errors.join("; ") || "Nenhum erro",
      "Token": result.result?.token || "N/A",
    }));

    // Criar workbook e worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Resultados");

    // Adicionar sheet de resumo
    const summaryData = [
      { "Métrica": "Total de Convênios", "Valor": totalAgreements },
      { "Métrica": "Sucessos", "Valor": successCount },
      { "Métrica": "Erros", "Valor": errorCount },
      { "Métrica": "Taxa de Sucesso", "Valor": totalAgreements > 0 ? `${((successCount / totalAgreements) * 100).toFixed(2)}%` : "0%" },
    ];
    const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summaryWorksheet, "Resumo");

    // Gerar arquivo Excel
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    
    // Nome do arquivo com data e hora
    const date = new Date();
    const dateStr = date.toISOString().split("T")[0];
    const timeStr = date.toTimeString().split(" ")[0].replace(/:/g, "-");
    const filename = `teste-integracao-massa_${dateStr}_${timeStr}.xlsx`;
    
    saveAs(blob, filename);
  };

  return (
    <Grid container width={1} spacing={2}>
      <Paper
        sx={{
          padding: "24px",
          width: "100%",
          backgroundColor:
            theme.palette.mode === "dark"
              ? theme.palette.background.paper
              : theme.palette.background.paper,
        }}
      >
        <Stack direction="column" spacing={3}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.text.primary
                  : theme.palette.text.primary,
            }}
          >
            Teste de Integração em Massa
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.text.secondary
                  : theme.palette.text.secondary,
            }}
          >
            Esta ferramenta testa a validação de integração para todos os
            convênios que possuem integração configurada usando apenas o CPF
            informado.
          </Typography>

          <FormProvider {...formMethods}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid size={{ xs: 12, md: 3 }}>
                <Form.Input
                  name="cpf"
                  label="CPF/CNPJ"
                  type="text"
                  mask="document"
                  required
                  fullWidth
                  disabled={isRunning}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Form.Select
                  name="integration_id"
                  label="Filtrar por Integração (opcional)"
                  options={[
                    { value: "", label: "Todas as integrações" },
                    ...integrationOptions,
                  ]}
                  fullWidth
                  disabled={isRunning}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Form.Select
                  name="product_module"
                  label="Filtrar por Módulo Super App (opcional)"
                  options={[
                    { value: "", label: "Todos" },
                    { value: "true", label: "Com Módulo Super App" },
                    { value: "false", label: "Sem Módulo Super App" },
                  ]}
                  fullWidth
                  disabled={isRunning}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={isRunning || !formMethods.watch("cpf")}
                  fullWidth
                  size="large"
                >
                  {isRunning ? "Testando..." : "Iniciar Testes"}
                </Button>
              </Grid>
            </Grid>
          </FormProvider>

          {isRunning && (
            <Box>
              <Stack spacing={2}>
                <Box>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Typography variant="body2" fontWeight="medium">
                      Progresso
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {processedCount} de {totalAgreements} convênios
                      processados ({Math.round(progressPercentage)}%)
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={progressPercentage}
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? theme.palette.grey[700]
                          : theme.palette.grey[200],
                      "& .MuiLinearProgress-bar": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? theme.palette.primary.light
                            : theme.palette.primary.main,
                      },
                    }}
                  />
                </Box>
                {currentIndex < totalAgreements && (
                  <Alert severity="info">
                    Testando: {agreements[currentIndex]?.name || "Carregando..."}
                  </Alert>
                )}
              </Stack>
            </Box>
          )}

          {!isRunning && results.length > 0 && (
            <Box>
              <Stack spacing={2} mb={3}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color:
                        theme.palette.mode === "dark"
                          ? theme.palette.text.primary
                          : theme.palette.text.primary,
                    }}
                  >
                    Resumo
                  </Typography>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<FileDownload />}
                    onClick={exportToExcel}
                    size="medium"
                  >
                    Exportar para Excel
                  </Button>
                </Stack>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        textAlign: "center",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? theme.palette.grey[800]
                            : theme.palette.grey[50],
                        border:
                          theme.palette.mode === "dark"
                            ? `1px solid ${theme.palette.grey[700]}`
                            : "none",
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          color:
                            theme.palette.mode === "dark"
                              ? theme.palette.primary.light
                              : theme.palette.primary.main,
                        }}
                      >
                        {totalAgreements}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total de Convênios
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        textAlign: "center",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? theme.palette.success.dark
                            : theme.palette.success.light,
                        border:
                          theme.palette.mode === "dark"
                            ? `1px solid ${theme.palette.success.main}`
                            : `1px solid ${theme.palette.success.main}`,
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          color: "#ffffff",
                        }}
                      >
                        {successCount}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#ffffff",
                        }}
                      >
                        Sucessos
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        textAlign: "center",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? theme.palette.error.dark
                            : theme.palette.error.light,
                        border:
                          theme.palette.mode === "dark"
                            ? `1px solid ${theme.palette.error.main}`
                            : `1px solid ${theme.palette.error.main}`,
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          color: "#ffffff",
                        }}
                      >
                        {errorCount}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#ffffff",
                        }}
                      >
                        Erros
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Stack>
            </Box>
          )}

          {results.length > 0 && (
            <Box>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  color:
                    theme.palette.mode === "dark"
                      ? theme.palette.text.primary
                      : theme.palette.text.primary,
                }}
              >
                Resultados Detalhados
              </Typography>
              <Stack spacing={2}>
                {results.map((result, index) => (
                  <Paper
                    key={result.agreement?.id || index}
                    elevation={2}
                    sx={{
                      p: 2,
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? theme.palette.grey[800]
                          : theme.palette.grey[50],
                      borderLeft: `4px solid ${
                        result.status === "success"
                          ? theme.palette.success.main
                          : result.status === "error"
                          ? theme.palette.error.main
                          : theme.palette.grey[400]
                      }`,
                      border:
                        theme.palette.mode === "dark"
                          ? `1px solid ${theme.palette.grey[700]}`
                          : `1px solid ${theme.palette.grey[300]}`,
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="subtitle1" fontWeight="bold">
                          {result.agreement?.name || "Convênio não encontrado"}
                        </Typography>
                        <Chip
                          icon={
                            result.status === "success" ? (
                              <CheckCircle />
                            ) : result.status === "error" ? (
                              <ErrorIcon />
                            ) : (
                              <CircularProgress size={16} />
                            )
                          }
                          label={
                            result.status === "success"
                              ? "Sucesso"
                              : result.status === "error"
                              ? "Erro"
                              : "Pendente"
                          }
                          color={
                            result.status === "success"
                              ? "success"
                              : result.status === "error"
                              ? "error"
                              : "default"
                          }
                          size="small"
                        />
                      </Stack>

                      {result.agreement?.integration_id && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Plataforma:</strong>{" "}
                          {result.agreement.integration_id}
                        </Typography>
                      )}

                      {result.errors.length > 0 && (
                        <Box
                          sx={{
                            mt: 1,
                            p: 1.5,
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? theme.palette.error.dark
                                : theme.palette.error.light,
                            borderRadius: 1,
                            border:
                              theme.palette.mode === "dark"
                                ? `1px solid ${theme.palette.error.main}`
                                : `1px solid ${theme.palette.error.main}`,
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{
                              color: "#ffffff",
                            }}
                            gutterBottom
                          >
                            Erros ({result.errors.length}):
                          </Typography>
                          <Stack spacing={0.5}>
                            {result.errors.map((error, errorIndex) => (
                              <Typography
                                key={errorIndex}
                                variant="body2"
                                sx={{
                                  color: "#ffffff",
                                }}
                              >
                                • {error}
                              </Typography>
                            ))}
                          </Stack>
                        </Box>
                      )}

                      {result.status === "success" && result.errors.length === 0 && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                          Integração funcionando corretamente!
                        </Alert>
                      )}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </Paper>
    </Grid>
  );
}

